import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";

export interface DealFilters {
  status?: string;
  dealType?: string;
  minAmount?: number;
  maxAmount?: number;
  startDateFrom?: string;
  startDateTo?: string;
  brandName?: string;
  athleteName?: string;
  riskLevel?: string;
  complianceStatus?: string;
  category?: string;
}

interface DealsFilterProps {
  filters: DealFilters;
  onFiltersChange: (filters: DealFilters) => void;
  showStatusFilter?: boolean;
}

export function DealsFilter({ filters, onFiltersChange, showStatusFilter = true }: DealsFilterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const updateFilter = (key: keyof DealFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="flex gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px]" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Filter Deals</h4>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-8 px-2"
                >
                  Clear all
                </Button>
              )}
            </div>

            <div className="space-y-4">
              {showStatusFilter && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(value) =>
                      updateFilter("status", value === "all" ? "" : value)
                    }
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending_compliance">Pending Compliance</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="dealType">Deal Type</Label>
                <Select
                  value={filters.dealType || "all"}
                  onValueChange={(value) =>
                    updateFilter("dealType", value === "all" ? "" : value)
                  }
                >
                  <SelectTrigger id="dealType">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="endorsement">Endorsement</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="appearance">Appearance</SelectItem>
                    <SelectItem value="merchandise">Merchandise</SelectItem>
                    <SelectItem value="autograph_signing">Autograph Signing</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="riskLevel">Risk Level</Label>
                <Select
                  value={filters.riskLevel || "all"}
                  onValueChange={(value) =>
                    updateFilter("riskLevel", value === "all" ? "" : value)
                  }
                >
                  <SelectTrigger id="riskLevel">
                    <SelectValue placeholder="All levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All levels</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minAmount">Min Amount</Label>
                  <Input
                    id="minAmount"
                    type="number"
                    placeholder="$0"
                    value={filters.minAmount || ""}
                    onChange={(e) =>
                      updateFilter("minAmount", e.target.value ? Number(e.target.value) : undefined)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxAmount">Max Amount</Label>
                  <Input
                    id="maxAmount"
                    type="number"
                    placeholder="No limit"
                    value={filters.maxAmount || ""}
                    onChange={(e) =>
                      updateFilter("maxAmount", e.target.value ? Number(e.target.value) : undefined)
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="brandName">Brand Name</Label>
                <Input
                  id="brandName"
                  placeholder="Search by brand..."
                  value={filters.brandName || ""}
                  onChange={(e) => updateFilter("brandName", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="athleteName">Athlete Name</Label>
                <Input
                  id="athleteName"
                  placeholder="Search by athlete..."
                  value={filters.athleteName || ""}
                  onChange={(e) => updateFilter("athleteName", e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDateFrom">Start Date From</Label>
                  <Input
                    id="startDateFrom"
                    type="date"
                    value={filters.startDateFrom || ""}
                    onChange={(e) => updateFilter("startDateFrom", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="startDateTo">Start Date To</Label>
                  <Input
                    id="startDateTo"
                    type="date"
                    value={filters.startDateTo || ""}
                    onChange={(e) => updateFilter("startDateTo", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {filters.status && (
            <Badge variant="secondary" className="gap-1">
              Status: {filters.status.replace(/_/g, " ")}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter("status", "")}
              />
            </Badge>
          )}
          {filters.dealType && (
            <Badge variant="secondary" className="gap-1">
              Type: {filters.dealType.replace(/_/g, " ")}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter("dealType", "")}
              />
            </Badge>
          )}
          {filters.riskLevel && (
            <Badge variant="secondary" className="gap-1">
              Risk: {filters.riskLevel}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter("riskLevel", "")}
              />
            </Badge>
          )}
          {(filters.minAmount || filters.maxAmount) && (
            <Badge variant="secondary" className="gap-1">
              Amount: ${filters.minAmount || 0} - ${filters.maxAmount || "∞"}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => {
                  updateFilter("minAmount", undefined);
                  updateFilter("maxAmount", undefined);
                }}
              />
            </Badge>
          )}
          {filters.brandName && (
            <Badge variant="secondary" className="gap-1">
              Brand: {filters.brandName}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter("brandName", "")}
              />
            </Badge>
          )}
          {filters.athleteName && (
            <Badge variant="secondary" className="gap-1">
              Athlete: {filters.athleteName}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter("athleteName", "")}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
